import { ICON_NAMES } from '@photosi/rn-components/icon/icon.helper';
import { destroy, detach, Instance, types } from 'mobx-state-tree';
import { DYNAMIC_MODAL_GROUPS, TOAST_TYPES } from './toastStore.types';
import { ToastInstance, ToastModel } from '../_models/toast';
import { ToastProps } from '@photosi/rn-components/toast/Toast.types';
import { text } from 'src/theme/colors';
import { TEXT_ICON_BUTTON_TYPES } from '@photosi/rn-components/buttons/text-icon-button/TextIconButton.constants';
import { ANIMATED_ICON } from '@photosi/rn-components/animated-icon/AnimatedIcon.constants';

export const ToastStore = types
  .model({
    toasts: types.optional(types.array(ToastModel), []),
    disableToastIds: types.optional(types.array(types.string), []),
  })
  .volatile<{
    data: string | undefined;
  }>(self => ({
    data: undefined,
  }))
  .views(self => ({
    get visibleToasts() {
      return self.toasts.filter(toast => !self.disableToastIds.includes(toast.id));
    },
  }))
  .actions(self => {
    const removeToastByIndex = (toastIndex: number) => {
      if (toastIndex !== -1) {
        // it is needed to save the element to remove in a const otherwise the destroy function does not find the element to destroy
        const toastToRemove = self.toasts[toastIndex];

        if (toastToRemove) {
          toastToRemove.onAfterClose?.();
          detach(toastToRemove); // remove the node from mobx tree
          destroy(toastToRemove); // destroy the node
        }
      }
    };
    const setData = (data: string | undefined) => {
      self.data = data;
    };

    return {
      removeToastByIndex,
      setData,
    };
  })
  .actions(self => {
    const createToast = function (toast: ToastProps): ToastInstance {
      const newToast = ToastModel.create({
        ...toast,
        message: toast.message || '',
        group: toast.group ?? DYNAMIC_MODAL_GROUPS.Generic_Toast,
        alreadyExists: false,
        isExiting: false,
      });
      newToast.attachEventHandlers(toast);
      return newToast;
    };

    const hideToast = function (id: string): void {
      const toastAlreadyExists = self.toasts.find(t => t.id === id);
      toastAlreadyExists?.setIsExiting(true);
    };

    // mobx has some problem on handling arrays, actually it is not clear why this action does not work if place with showToast action
    const removeToast = (id: string) => {
      const toastIndex = self.toasts.findIndex(toast => toast.id === id);
      self.removeToastByIndex(toastIndex);
    };

    const showToast = function (toast: ToastProps): ToastInstance | undefined {
      const toastAlreadyExists = self.toasts.find(t => t.group === toast.group);
      if (toast.id && self.disableToastIds.includes(toast.id)) {
        // the toast is disabled, doesn't show anything
        return undefined;
      } else if (toastAlreadyExists) {
        // only one toast for each group can be shown, the new toast will replace the old one
        // update the existing toast
        // set the isExiting to false to reset the exit animation
        toastAlreadyExists.update({ ...toast, alreadyExists: true, isExiting: false });
        return toastAlreadyExists;
      } else {
        // create a new toast
        const newToast = createToast(toast);
        self.toasts.push(newToast);
        return newToast;
      }
    };

    const showQualityAlertToast = function (toast: Omit<ToastProps, 'group'>) {
      return showToast({
        ...toast,
        icon: ICON_NAMES.Alert,
        iconColor: text.secondaryText,
        group: DYNAMIC_MODAL_GROUPS.QualityAlertToast,
      });
    };

    const showMinProductMissingToast = function (toast: Omit<ToastProps, 'group'>) {
      return showToast({
        ...toast,
        icon: ICON_NAMES.Alert,
        iconColor: text.secondaryText,
        group: DYNAMIC_MODAL_GROUPS.MinProductMissingToast,
        type: TOAST_TYPES.Info,
        ctaIconType: TEXT_ICON_BUTTON_TYPES.ONLY_CTA,
      });
    };

    const showCutsCheckAlertToast = function (toast: Omit<ToastProps, 'group'>) {
      return showToast({
        ...toast,
        icon: ICON_NAMES.Cut,
        iconColor: text.secondaryText,
        group: DYNAMIC_MODAL_GROUPS.CutsCheckAlertToast,
        ctaIconType: TEXT_ICON_BUTTON_TYPES.ONLY_CTA,
      });
    };

    const showErrorToast = function (toast: Omit<ToastProps, 'group'>) {
      return showToast({
        ...toast,
        icon: ICON_NAMES.Alert,
        group: DYNAMIC_MODAL_GROUPS.ErrorToast,
        duration: 10000,
        type: TOAST_TYPES.Error,
      });
    };

    const showInfoToast = function (toast: Omit<ToastProps, 'group'>) {
      return showToast({
        ...toast,
        group: DYNAMIC_MODAL_GROUPS.Generic_Toast,
        type: TOAST_TYPES.Info,
      });
    };

    const showLoadingToast = function (toast: Omit<ToastProps, 'group'>) {
      return showToast({
        ...toast,
        group: DYNAMIC_MODAL_GROUPS.Loading_Toast,
        type: TOAST_TYPES.Info,
        duration: 1500,
        loader: true,
      });
    };

    const showProjectProductsNotUploadedToast = function (toast: Omit<ToastProps, 'group'>) {
      return showToast({
        ...toast,
        icon: ICON_NAMES.Alert,
        iconColor: text.secondaryText,
        group: DYNAMIC_MODAL_GROUPS.ProjectProductsNotUploaded,
        type: TOAST_TYPES.Info,
      });
    };

    const showHappyCaseOrPhotokitCompletedToast = function (toast: Omit<ToastProps, 'group'>) {
      return showToast({
        ...toast,
        group: DYNAMIC_MODAL_GROUPS.HappyCaseOrPhotokitCompleted,
        type: TOAST_TYPES.Info,
        animatedIcon: ANIMATED_ICON.CheckMark,
      });
    };

    const showOnDuplicateToast = function (toast: Omit<ToastProps, 'group'>) {
      return showToast({
        ...toast,
        group: DYNAMIC_MODAL_GROUPS.DuplicateToast,
        duration: 4000,
      });
    };

    const disableToast = function (toastId: string) {
      if (!self.disableToastIds.includes(toastId)) {
        self.disableToastIds.push(toastId);
      }
    };

    const enableToast = function (toastId: string) {
      self.disableToastIds.remove(toastId);
    };

    return {
      hideToast,
      removeToast,
      showToast,
      showQualityAlertToast,
      showCutsCheckAlertToast,
      showErrorToast,
      disableToast,
      enableToast,
      showInfoToast,
      showLoadingToast,
      showMinProductMissingToast,
      showProjectProductsNotUploadedToast,
      showHappyCaseOrPhotokitCompletedToast,
      showOnDuplicateToast,
    };
  })
  .actions(self => {
    const removeGroupToast = (group: string) => {
      self.toasts.forEach((toast, index) => {
        if (toast.group === group) {
          self.removeToastByIndex(index);
        }
      });
    };

    return { removeGroupToast };
  });

export type ToastStoreInstance = Instance<typeof ToastStore>;
